import React from "react";
import { Link } from "react-router-dom";
import { BrandLockup } from "../components/Brand";
import {
  ArrowLeft, PlayCircle, Music2, Tv, Youtube, Trophy, Users,
  Wifi, Maximize2, QrCode, ShieldCheck, ChevronRight,
} from "lucide-react";

/* Minimal, printable teacher usage guide. Uses existing dark/cyan/violet palette. */
export default function TeacherGuide() {
  return (
    <div className="min-h-screen text-white relative"
         style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}>
      <div className="hero-spectrum absolute inset-0" aria-hidden />
      <header className="relative z-10 px-6 sm:px-10 py-6 flex items-center justify-between">
        <BrandLockup />
        <Link to="/" data-testid="guide-back-home"
              className="text-sm text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4"/> Ana Sayfa
        </Link>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-6 sm:px-10 pb-24" data-testid="teacher-guide">
        <div className="text-cyan-300 font-heading tracking-widest text-xs">ÖĞRETMEN REHBERİ</div>
        <h1 className="mt-2 font-heading text-5xl font-bold">Sınıfta 5 dakikada başla</h1>
        <p className="mt-3 text-zinc-400 font-literary italic text-lg max-w-2xl">
          Kısa bir kullanım kılavuzu — oyun kur, sınıfa yansıt, YouTube'u kontrol et, öğrencilerini
          liderlikte topla.
        </p>

        {/* Steps */}
        <ol className="mt-10 space-y-6">
          <Step n={1} icon={<PlayCircle className="h-5 w-5"/>} title="Giriş yap">
            <p>Sağ üstteki <span className="text-cyan-300">Öğretmen Girişi</span> ile giriş yap.
              Test hesabı: <code className="text-cyan-200">admin@sarkideyim.tr</code>.</p>
          </Step>

          <Step n={2} icon={<Music2 className="h-5 w-5"/>} title="Paket seç">
            <p>Panelden <b>Tarkan Özel</b>, <b>Barış Manço Özel</b> ya da <b>Karışık Şarkılar</b>
              paketinden birini seç.</p>
          </Step>

          <Step n={3} icon={<Music2 className="h-5 w-5"/>} title="Şarkıları seç">
            <p>Şarkı kartlarına dokunarak istediğin kadar şarkı seç. <b>Tümünü Seç</b> ile hepsini
              hızla ekle. Alt kısımda seçtiğin oyunun toplam <span className="text-cyan-300">şarkı /
                deyim / soru</span> sayısı otomatik güncellenir.</p>
          </Step>

          <Step n={4} icon={<Users className="h-5 w-5"/>} title="Öğrencileri davet et">
            <p><b>Oyunu Oluştur</b> dedikten sonra ekranda 6 haneli <b>SINIF KODU</b> çıkar.
              Öğrenciler telefonlarından <code className="text-cyan-200">/join</code>'a giderek bu
              kodla katılır — hesap gerekmez.</p>
          </Step>

          <Step n={5} icon={<Tv className="h-5 w-5"/>} title="Akıllı tahtaya yansıt" testId="guide-projection">
            <p>Lobide veya oyunda üstteki <span className="text-cyan-300">⋯ Oyun Menüsü → 📺 Sınıfa
              Yansıt</span>'ı seç. Projeksiyon ekranında <b>büyük sınıf kodu</b> ve <b>QR</b>
              görüntülenir; öğrenciler kamerayla okuyup tek dokunuşla katılır.</p>
            <div className="mt-3 rounded-xl border border-cyan-400/20 bg-cyan-500/5 p-3 text-sm text-cyan-100/90">
              🎯 <b>İpucu:</b> <span className="text-white"><Maximize2 className="inline h-4 w-4"/> Tam
              Ekran</span> tuşuyla tarayıcı çubuğunu gizle — projektörde daha temiz durur.
            </div>
          </Step>

          <Step n={6} icon={<Youtube className="h-5 w-5 text-red-400"/>} title="Şarkıyı çal" testId="guide-youtube">
            <p>Dinleme aşamasında <span className="text-red-300">▶ YouTube'da Dinlet</span>
              düğmesine bas. <b>YouTube yalnızca senin cihazında yeni bir sekmede açılır.</b> Akıllı
              tahtada oyun ekranı görünür kalır — sınıf yalnızca sesi duyar, video yansımaz.</p>
            <ul className="mt-3 space-y-1 text-sm text-zinc-300 list-disc pl-5">
              <li>Şarkı bitince sekmesini kapat, oyunun durumu bozulmaz.</li>
              <li><b>SORULARI BAŞLAT →</b> düğmesine bas, ilk soru yansır.</li>
            </ul>
          </Step>

          <Step n={7} icon={<Trophy className="h-5 w-5"/>} title="Liderliği yönet">
            <p>Her deyimden sonra kısa bir <b>Anlık Liderlik</b>, her şarkıdan sonra <b>Şarkı Sonu
              Liderlik</b>, ve sonda <b>Final Podyum</b> gösterilir. Üst çubuktaki
              <Trophy className="inline h-4 w-4 text-cyan-300 mx-1"/><b>Liderlik</b> düğmesiyle
              istediğin an ara vermeden tabloya bakabilirsin.</p>
          </Step>

          <Step n={8} icon={<Wifi className="h-5 w-5"/>} title="Bağlantı kopunca">
            <p>Öğretmen veya öğrenci internetten koparsa, sayfa yeniden bağlandığında oyun kaldığı
              yerden devam eder. Öğrenci daha önce verdiği cevabı yeniden gönderemez — puanlar
              korunur.</p>
          </Step>

          <Step n={9} icon={<ShieldCheck className="h-5 w-5"/>} title="Oyunu bitir">
            <p>Üst çubuktan <span className="text-rose-300">← Oyundan Çık</span> ile onayla,
              ardından <b>📊 Oyun Raporunu Gör</b> ile sınıf performansını incele. Öğrenciler kendi
              telefonunda kişisel öğrenme kartlarını görür.</p>
          </Step>
        </ol>

        {/* Quick reference */}
        <section className="mt-14 rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8"
                 data-testid="guide-quickref">
          <div className="text-xs font-heading tracking-widest text-cyan-300">HIZLI REFERANS</div>
          <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
            <Ref icon={<QrCode className="h-4 w-4"/>} label="QR ile katılım"
                 desc="Projeksiyon lobisi öğrencilerin kamera ile okuyabileceği QR gösterir."/>
            <Ref icon={<Maximize2 className="h-4 w-4"/>} label="Tam ekran"
                 desc="Projeksiyon ekranında ⛶ Tam Ekran ile tarayıcı barını gizle."/>
            <Ref icon={<Tv className="h-4 w-4"/>} label="Yansıtma modu"
                 desc="Menü → 📺 Sınıfa Yansıt. Oyun durumu bozulmaz."/>
            <Ref icon={<Youtube className="h-4 w-4 text-red-400"/>} label="YouTube gizliliği"
                 desc="Video sadece öğretmen cihazında yeni sekmede açılır — akıllı tahtaya yansımaz."/>
            <Ref icon={<Trophy className="h-4 w-4"/>} label="Liderlik"
                 desc="Üst çubuktan istediğin an aç. Kapatınca oyun kaldığı yerden sürer."/>
            <Ref icon={<Wifi className="h-4 w-4"/>} label="Reconnect"
                 desc="Sayfa yenilendiğinde oyun aşaması korunur; puanlar iki kez sayılmaz."/>
          </div>
        </section>

        {/* CTA */}
        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/login" data-testid="guide-start-cta"
                className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold px-6 py-3">
            Öğretmen olarak başla →
          </Link>
        </div>
      </main>
    </div>
  );
}

function Step({ n, icon, title, children, testId }) {
  return (
    <li className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6 flex gap-5"
        data-testid={testId}>
      <div className="shrink-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 font-heading font-bold">
          {n}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-cyan-200">{icon}<h3 className="font-heading font-bold text-lg">{title}</h3></div>
        <div className="mt-1 text-zinc-300 text-sm sm:text-[15px] leading-relaxed">{children}</div>
      </div>
    </li>
  );
}

function Ref({ icon, label, desc }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-4">
      <div className="flex items-center gap-2 text-cyan-200 font-heading text-sm">{icon} {label}</div>
      <div className="mt-1 text-zinc-400 text-xs">{desc}</div>
    </div>
  );
}
